import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, KeyRound, MessageCircle, Plug, User } from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Spinner } from "@/shared/ui/spinner.jsx";
import { toast } from "@/shared/ui/toaster.jsx";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { authApi } from "@/api/endpoints/auth.js";
import { useWhatsappConfig } from "@/features/whatsapp/use-whatsapp.js";

/**
 * Preferências da clínica e da conta.
 *
 * Além dos atalhos de integração que já existiam, ganhou a troca de senha —
 * no app antigo não havia como um usuário mudar a própria senha.
 */
export function SettingsPage() {
  const { user } = useAuth();
  const { data: whatsapp } = useWhatsappConfig();

  return (
    <AppShell title="Configurações" subtitle="Preferências, integrações e conexões da clínica">
      <div className="mx-auto max-w-5xl space-y-8 p-6">
        {/* ------------------------------------------------------ conta */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Sua conta</h2>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">Dados</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Linha rotulo="Nome" valor={user?.fullName} />
                <Linha rotulo="E-mail" valor={user?.email} />
                <Linha rotulo="Perfil" valor={user?.roleLabel} />
                <Linha rotulo="Clínica" valor={user?.clinicName ?? "—"} />
              </CardContent>
            </Card>

            <TrocarSenha />
          </div>
        </section>

        {/* ------------------------------------------------ integrações */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Plug className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-semibold">Integrações</h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Link to="/integracoes/whatsapp-meta" className="rounded-xl">
              <Card className="group flex h-full flex-col gap-3 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#25D366]/20 to-[#128C7E]/20 text-[#128C7E]">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <Badge variant={whatsapp?.connected ? "success" : "secondary"} className="text-[10px]">
                    {whatsapp?.connected ? "Conectado" : "Disponível"}
                  </Badge>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-base font-semibold">WhatsApp Meta API</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Conecte o número oficial: App ID, Cloud API Token, número conectado e status da
                    integração.
                  </p>
                </div>
                <div className="flex items-center gap-1 text-sm font-medium text-primary">
                  Configurar
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Card>
            </Link>

            <Link to="/integracoes" className="rounded-xl">
              <Card className="group flex h-full flex-col gap-3 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-chart-2/15 text-primary">
                    <Plug className="h-5 w-5" />
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    Ver todas
                  </Badge>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-base font-semibold">Central de Integrações</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Meta Ads, Pagamentos, Google Agenda e outras conexões disponíveis.
                  </p>
                </div>
                <div className="flex items-center gap-1 text-sm font-medium text-primary">
                  Abrir
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Card>
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function TrocarSenha() {
  const { clearSession } = useAuth();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [enviando, setEnviando] = useState(false);

  const set = (campo) => (e) => setForm((atual) => ({ ...atual, [campo]: e.target.value }));

  const valido =
    form.currentPassword && form.newPassword.length >= 8 && form.newPassword === form.confirm;

  async function submeter(evento) {
    evento.preventDefault();
    if (!valido) return;

    setEnviando(true);
    try {
      await authApi.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      toast.success("Senha alterada. Entre novamente.");
      // A troca de senha derruba todas as sessões — inclusive esta.
      clearSession();
    } catch (erro) {
      toast.error(erro.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 font-display text-base">
          <KeyRound className="h-4 w-4 text-primary" />
          Alterar senha
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Trocar a senha encerra as sessões abertas em outros dispositivos.
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={submeter} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="senha-atual">Senha atual</Label>
            <Input
              id="senha-atual"
              type="password"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={set("currentPassword")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="senha-nova">Nova senha</Label>
            <Input
              id="senha-nova"
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={set("newPassword")}
            />
            <p className="text-xs text-muted-foreground">Mínimo de 8 caracteres.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="senha-confirma">Confirmar nova senha</Label>
            <Input
              id="senha-confirma"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={set("confirm")}
            />
            {form.confirm && form.newPassword !== form.confirm && (
              <p className="text-xs text-destructive">As senhas não conferem.</p>
            )}
          </div>

          <Button type="submit" disabled={!valido || enviando} className="w-full">
            {enviando ? <Spinner /> : null}
            Alterar senha
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Linha({ rotulo, valor }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2 last:border-0">
      <span className="text-muted-foreground">{rotulo}</span>
      <span className="truncate font-medium">{valor || "—"}</span>
    </div>
  );
}
